/**
 * Single KPI tile: title + big value + delta vs previous period + a
 * tiny sparkline. ``loading=true`` renders a same-shape skeleton so
 * the grid doesn't shift when data lands.
 */
import { type ReactNode } from "react";
import { ArrowDownRight, ArrowRight, ArrowUpRight, type LucideIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface KpiCardProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  delta?: { value: string; direction: "up" | "down" | "flat" } | null;
  /** Positive delta direction for "good" — green; for cost/error KPIs invert by setting goodDirection="down" */
  goodDirection?: "up" | "down";
  icon?: LucideIcon;
  sparkline?: ReadonlyArray<number>;
  loading?: boolean;
  accent?: "primary" | "success" | "warning" | "destructive" | "info";
}

const ACCENT_BG: Record<NonNullable<KpiCardProps["accent"]>, string> = {
  primary: "bg-primary/10 text-primary",
  success: "bg-success/10 text-success",
  warning: "bg-warning/15 text-warning",
  destructive: "bg-destructive/10 text-destructive",
  info: "bg-info/10 text-info",
};

const SPARK_STROKE: Record<NonNullable<KpiCardProps["accent"]>, string> = {
  primary: "hsl(var(--chart-1))",
  success: "hsl(var(--chart-2))",
  warning: "hsl(var(--chart-3))",
  destructive: "hsl(var(--destructive))",
  info: "hsl(var(--chart-4))",
};

export function KpiCard({
  label,
  value,
  hint,
  delta,
  goodDirection = "up",
  icon: Icon,
  sparkline,
  loading,
  accent = "primary",
}: KpiCardProps) {
  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        {Icon ? (
          <div className={cn("rounded-md p-1.5", ACCENT_BG[accent])}>
            <Icon className="h-3.5 w-3.5" />
          </div>
        ) : null}
      </div>
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          {loading ? (
            <Skeleton className="h-8 w-24" />
          ) : (
            <div className="truncate font-mono text-2xl font-semibold tracking-tight text-foreground">
              {value}
            </div>
          )}
          <div className="mt-1 flex items-center gap-2 text-xs">
            {delta ? <DeltaPill delta={delta} goodDirection={goodDirection} /> : null}
            {hint ? <span className="text-muted-foreground">{hint}</span> : null}
          </div>
        </div>
        {sparkline && sparkline.length > 1 ? (
          <div className="h-10 w-24 shrink-0 sm:w-32">
            <Sparkline data={sparkline} stroke={SPARK_STROKE[accent]} />
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function DeltaPill({
  delta,
  goodDirection,
}: {
  delta: NonNullable<KpiCardProps["delta"]>;
  goodDirection: NonNullable<KpiCardProps["goodDirection"]>;
}) {
  const isFlat = delta.direction === "flat";
  const isGood = !isFlat && delta.direction === goodDirection;
  const Icon = isFlat ? ArrowRight : delta.direction === "up" ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded font-medium",
        isFlat
          ? "text-muted-foreground"
          : isGood
            ? "text-success"
            : "text-destructive",
      )}
    >
      <Icon className="h-3 w-3" />
      {delta.value}
    </span>
  );
}

/**
 * Tiny inline-SVG sparkline (PR #136 review: recharts was ~150 KB gzipped
 * for a 12-point area chart with no axes — overkill for the dashboard
 * tiles). Stretches to fill its parent via ``preserveAspectRatio="none"``.
 */
function Sparkline({ data, stroke }: { data: ReadonlyArray<number>; stroke: string }) {
  if (data.length < 2) return null;
  const W = 100;
  const H = 30;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1; // avoid div-by-zero on a flat series
  const xStep = W / (data.length - 1);
  const points = data.map((v, i) => {
    const x = i * xStep;
    const y = H - ((v - min) / range) * H;
    return [x, y] as const;
  });
  const linePath =
    "M " + points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" L ");
  const areaPath = `${linePath} L ${W},${H} L 0,${H} Z`;
  // ``react/jsx-key`` is satisfied — we use a static gradient id derived
  // from the stroke value so multiple tiles can share the cache.
  const gradId = `spark-grad-${stroke.replace(/[^a-zA-Z0-9-]/g, "")}`;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      width="100%"
      height="100%"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity={0.3} />
          <stop offset="100%" stopColor={stroke} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
      <path d={linePath} stroke={stroke} strokeWidth={1.5} fill="none" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
